import * as React from 'react';

import { InfoCircleOutline, QuestionCircleOutline } from '@freelancelabs/icons';
import { eventBus } from '@freelancelabs/shared';
import { clsx } from '@freelancelabs/utils';

import { generateUID } from '../../../helpers';
import { Popper, PopperPlacement, usePopper, usePopperAnchor } from '../popper';
import useTooltipHandlers from '../tooltip/useTooltipHandlers';

interface Props {
    originalPlacement?: PopperPlacement;
    title?: React.ReactNode;
    buttonClass?: string;
    buttonTabIndex?: number;
    className?: string;
    questionMark?: boolean;
    id?: string;
    onOpenEventName?: string;
}

const Info = ({
    originalPlacement = 'top',
    title = undefined,
    buttonClass = 'tooltip-info',
    buttonTabIndex,
    className = '',
    questionMark = false,
    id,
    onOpenEventName,
    ...rest
}: Props) => {
    const [uid] = React.useState(generateUID('tooltip'));

    const { open, close, isOpen } = usePopperAnchor<HTMLButtonElement>();
    const { floating, reference, position, arrow, placement } = usePopper({
        isOpen,
        originalPlacement,
    });

    const handleClick = (event: React.MouseEvent) => {
        event.preventDefault();
        open();
    };

    const tooltipHandlers = useTooltipHandlers({ open, close, isOpen });
    React.useEffect(() => {
        if (isOpen && id && onOpenEventName) {
            eventBus.emit(onOpenEventName, { id });
        }
    }, [isOpen]);
    return (
        <>
            <button
                tabIndex={buttonTabIndex}
                className={buttonClass}
                onClick={handleClick}
                ref={reference}
                {...tooltipHandlers}
                aria-describedby={uid}
                type="button"
            >
                {questionMark ? (
                    <QuestionCircleOutline className={className} {...rest} />
                ) : (
                    <InfoCircleOutline className={className} {...rest} />
                )}
            </button>
            {title && isOpen ? (
                <Popper
                    divRef={floating}
                    id={uid}
                    isOpen={isOpen}
                    style={{ ...position, ...arrow }}
                    className={clsx('tooltip', `tooltip--${placement}`)}
                >
                    {title}
                </Popper>
            ) : null}
        </>
    );
};

export default Info;
